import { STAFF_APP_ROUTES } from 'notable-common';
import { matchPath } from 'react-router-dom';

import { isDefined } from 'common';

/** Keep in sync with the staff routes, which are nested JSX built from match.path and so cannot be derived. */
const telemetryRouteTemplates = [
  ...Object.values(STAFF_APP_ROUTES).map((route) => route.path),
  '/',
  '/staff/',
  '/staff/app/',
  '/login',
  '/login-success',
  '/terms-of-use',
  '/encounters',
  '/entity/encounters/:encounterId',
  '/entity/orders/:orderId',
  '/entity/externalDocumentPackets/:externalDocumentPacketId',
  '/entity/scenario-test',
  '/flow-tester/patient/create',
  '/flow-tester/patient/:ehrInformationId/create-order',
  '/flow-tester/patient/:ehrInformationId/create-encounter',
  '/flow-tester/patient/:ehrInformationId/edit',
  '/flow-tester/patient/:entityId',
  '/flow-tester/encounter/:encounterId/edit',
  '/flow-tester/encounter/:entityId',
  '/flow-tester/order/:orderId/edit',
  '/flow-tester/order/:entityId',
  '/flow-tester/externalDocumentPacket/create',
  '/flow-tester/externalDocumentPacket/:entityId',
  '/agents/flows/create/:templateId/edit',
  '/agents/flows/:flowId/insights',
  '/agents/flows/:flowId/explore-runs',
  '/agents/flows/:flowId/test-cases/:testCaseId',
  '/agents/flows/testPatientData/:ehrInformationId',
  '/agents/flows/referenceTables',
  '/agents/flows/referenceTables/:referenceTableId',
  '/agents/flows/standardAnalyticEvents',
  '/agents/flows/standardAnalyticEvents/categories',
  '/agents/fleets/:fleetId',
  '/agents/agents/:agentId',
  '/agents/agents/:agentId/insights',
  '/agents/agents/:agentId/flows/create',
  '/agents/agents/:agentId/flows/create/:templateId',
  '/agents/agents/:agentId/flows/create/:templateId/edit',
  '/agents/agents/:agentId/flows/:flowId',
  '/agents/agents/:agentId/flows/:flowId/insights',
  '/agents/agents/:agentId/flows/:flowId/explore-runs',
  '/human-tasks/external/:externalHumanTaskId',
  '/human-tasks/:humanTaskId',
  '/agentic-chat-logs/:sessionId',
  '/flow-compass-logs/:sessionId',
  '/messaging/templates',
  '/messaging/messages',
].sort(
  (first, second) =>
    second.split('/').filter((segment) => !segment.startsWith(':')).length -
    first.split('/').filter((segment) => !segment.startsWith(':')).length
);

/** Return only trusted route literals; unmatched paths must not reveal record identifiers or text. */
export function normalizeTelemetryRoute(path: string): string {
  const pathname = path.split(/[?#]/)[0];
  for (const template of telemetryRouteTemplates) {
    if (isDefined(matchPath(pathname, { path: template, exact: true, sensitive: true }))) {
      return template;
    }
  }
  for (const root of [
    'agents',
    'agentic-chat-logs',
    'api-instances',
    'appointment-kinds',
    'assignment-groups',
    'assistant',
    'assistants',
    'connections',
    'connectors',
    'contact-status',
    'decision-trees',
    'diagnostics',
    'document-templates',
    'ehr-instances',
    'ehr-systems',
    'encounters',
    'entity',
    'external-doc-packets',
    'feeds',
    'findings',
    'fingerprint',
    'flow-activity',
    'flow-compass-logs',
    'flow-tester',
    'front-desk-devices',
    'human-task-categories',
    'human-tasks',
    'impersonation',
    'job-configs',
    'knowledge-base',
    'messaging',
    'mindtickle-sso',
    'networking',
    'notes',
    'note-templates',
    'notifications',
    'organizations',
    'payments',
    'payors',
    'payouts',
    'permission-groups',
    'phone-numbers',
    'practice-configs',
    'practice-config-api-instances',
    'practice-config-ehr-instances',
    'practice-groups',
    'practices',
    'previsit-form-templates',
    'previsit-links',
    'providers',
    'recommendation-templates',
    'reports',
    'service-accounts',
    'signals',
    'stripe-accounts',
    'tasks',
    'triggers',
    'users',
    'utilization',
    'version',
  ]) {
    if (pathname === `/${root}` || pathname === `/${root}/`) {
      return `/${root}`;
    }
    if (pathname.startsWith(`/${root}/`)) {
      return `/${root}/:path`;
    }
  }
  return '/:unrecognizedRoute';
}
